import { playSlab } from '../../audio/slab-sound.js';
import { element as el } from '../dom.js';

type Edge = 'right' | 'left' | 'bottom';
const LEAVE_MS = 500;
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
/** Nothing slides, and nothing is heard, while the door is still shut. */
const doorShut = () => document.getElementById('gate')?.hidden === false;

/** Slides one stone slab in from `edge` with its scrape, replaying the animation if it is already running. */
export function slideIn(target: HTMLElement, edge: Edge) {
  if (reduced() || doorShut()) return;
  const name = `slab-in-${edge}`;
  target.classList.remove('slab-in-right', 'slab-in-left', 'slab-in-bottom'); void target.offsetWidth; target.classList.add(name);
  const done = (event: AnimationEvent) => { if (event.target !== target) return; target.classList.remove(name); target.removeEventListener('animationend', done); };
  target.addEventListener('animationend', done);
  playSlab();
}

const lobbySlabs = () => Array.from(el('lobby').children).filter((child): child is HTMLElement =>
  child instanceof HTMLElement && child.hasAttribute('data-for') && !child.classList.contains('lobby-logo') && getComputedStyle(child).display !== 'none');

let leaving = 0;
/** The lobby's slabs slide away to the left as the world arrives, then the lobby goes. */
export function leaveLobby() {
  const lobby = el('lobby'), token = ++leaving;
  if (reduced() || lobby.hidden) { lobby.hidden = true; return; }
  lobby.classList.add('leaving'); playSlab();
  setTimeout(() => { if (token !== leaving) return; lobby.classList.remove('leaving'); lobby.hidden = true; }, LEAVE_MS);
}
/** The lobby is wanted again, so a slide-out in progress must not hide it. */
export function keepLobby() { leaving++; el('lobby').classList.remove('leaving'); }

/**
 * Every step of the flow arrives as a slab: the lobby's pages and the sign-in, room and party steps from the right, the
 * in-game panels from the left and the HUD from below. Anything that was visible already is left alone.
 */
export function watchSlabs() {
  const edges: [string[], Edge][] = [
    [['auth-panel', 'room-panel', 'party-panel'], 'right'],
    [['behavior-panel', 'supplies-panel', 'loot-panel'], 'left'],
    // Rest opens from beside the clock, in the bottom right corner.
    [['rest-panel'], 'right'],
    [['hud'], 'bottom'],
  ];
  const shown = new WeakMap<HTMLElement, boolean>(), edgeOf = new WeakMap<HTMLElement, Edge>();
  const lobby = el('lobby');
  for (const [ids, edge] of edges) for (const id of ids) { const target = el(id); edgeOf.set(target, edge); shown.set(target, !target.hidden); }
  shown.set(lobby, !lobby.hidden);
  const hidden = new MutationObserver(records => {
    for (const record of records) {
      const target = record.target as HTMLElement, visible = !target.hidden;
      if (shown.get(target) === visible) continue;
      shown.set(target, visible);
      if (!visible) continue;
      if (target === lobby) lobbySlabs().forEach(slab => slideIn(slab, 'right'));
      else slideIn(target, edgeOf.get(target)!);
    }
  });
  for (const target of [lobby, ...edges.flatMap(([ids]) => ids.map(el))]) hidden.observe(target, { attributes: true, attributeFilter: ['hidden'] });
  new MutationObserver(() => lobbySlabs().forEach(slab => slideIn(slab, 'right'))).observe(lobby, { attributes: true, attributeFilter: ['data-view'] });
}
